import { useQuery, useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { useToast } from "../ui/use-toast";
import {
  Account,
  SuccessResponse,
  AccountContactsMetadata,
} from "../../types/generated";
import { apiService } from "../../services/api";
import { Download, Loader2 } from "lucide-react";
import { CopyIdButton } from "../ui/copy-id-button";

interface UserAccountsTabProps {
  userId: string;
  userName: string;
}

export function UserAccountsTab({ userId, userName }: UserAccountsTabProps) {
  const { toast } = useToast();
  const [loadingCsv, setLoadingCsv] = useState<string | null>(null);
  const { data: accounts, isLoading: accountsLoading } = useQuery<
    SuccessResponse<Account[]>
  >({
    queryKey: ["user-accounts", userId],
    queryFn: () => apiService.getUserAccounts(userId),
  });

  const contactsQueries = useQueries({
    queries: (accounts?.data || []).map((account) => ({
      queryKey: ["account-contacts", account.id],
      queryFn: () => apiService.getAccountContactsMetadata(account.id),
      enabled: !!account.id,
    })),
  });

  const isLoading =
    accountsLoading || contactsQueries.some((query) => query.isLoading);

  const getContactsCount = (accountId: string) => {
    // Find the query result for this account in the same order as the accounts array
    const accountIndex =
      accounts?.data.findIndex((a) => a.id === accountId) ?? -1;
    const query = contactsQueries[accountIndex];
    return query?.data?.data.total || 0;
  };

  const downloadContactsAsCsv = async (accountId: string, accountName: string) => {
    setLoadingCsv(accountId);
    try {
      const response = await apiService.getContacts(accountId);
      
      // Ensure we have an array of contacts from the API response
      const contacts = Array.isArray(response.data?.contacts) ? response.data.contacts : [];
      
      if (contacts.length === 0) {
        toast({
          title: "No Contacts",
          description: "No contacts found for this account",
          variant: "destructive"
        });
        return;
      }
      
      // Define specific headers we want and their display names
      const headers = ["phone", "FullName", "Found"];
      
      // Create CSV header row
      let csvContent = headers.join(",") + "\n";
      
      // Add rows with only the specific fields we want
      contacts.forEach(contact => {
        const row = headers.map(header => {
          // Get the value - handle special fields inside the info object
          let value;
          if (header === "phone") {
            value = contact.id;
          } else if (header === "FullName" || header === "Found") {
            value = contact.info?.[header];
          }
          
          // Handle different types of values
          const cellValue = value === null || value === undefined 
            ? "" 
            : typeof value === "boolean"
              ? String(value)
              : String(value).replace(/"/g, '""');
          // Wrap in quotes if contains comma, newline or quotes
          return cellValue.includes(",") || cellValue.includes("\n") || cellValue.includes('"') 
            ? `"${cellValue}"` 
            : cellValue;
        });
        csvContent += row.join(",") + "\n";
      });
      
      // Create a blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${accountName.replace(/\s+/g, "-")}-contacts.csv`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      // Show success toast
      toast({
        title: "Download Started",
        description: `${contacts.length} contacts exported for ${accountName}`,
      });
      
    } catch (error) {
      console.error("Error downloading contacts:", error);
      toast({
        title: "Download Failed",
        description: "Failed to download contacts. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingCsv(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop View */}
      <div className="hidden md:block border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Name</TableHead>
              <TableHead className="whitespace-nowrap">Phone Number</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="whitespace-nowrap">Privacy Mode</TableHead>
              <TableHead className="whitespace-nowrap">External ID</TableHead>
              <TableHead className="whitespace-nowrap">Account ID</TableHead>
              <TableHead className="whitespace-nowrap">
                Total Contacts
              </TableHead>
              <TableHead className="whitespace-nowrap">Last Updated</TableHead>
              <TableHead className="whitespace-nowrap">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts?.data.map((account) => (
              <TableRow key={account.id}>
                <TableCell>{account.name}</TableCell>
                <TableCell>{account.wa_id || "-"}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      account.status === "connected"
                        ? "success"
                        : account.status === "disconnected"
                        ? "destructive"
                        : "secondary"
                    }
                    className="capitalize"
                  >
                    {account.status}
                  </Badge>
                </TableCell>
                <TableCell className="capitalize">
                  {account.settings?.privacy_mode || "default"}
                </TableCell>
                <TableCell>
                  <CopyIdButton id={account.external_id} />
                </TableCell>
                <TableCell>
                  <CopyIdButton id={account.id} />
                </TableCell>
                <TableCell>{account?.metadata?.contacts?.total || getContactsCount(account.id)}</TableCell>
                <TableCell>
                  {account.deleted_at ? (
                    <span className="text-destructive">Deleted</span>
                  ) : (
                    format(new Date(), "PPP")
                  )}
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => downloadContactsAsCsv(account.id, account.name)}
                    disabled={account.status !== "connected" || loadingCsv === account.id}
                  >
                    {loadingCsv === account.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-1" />
                        CSV
                      </>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(!accounts?.data || accounts.data.length === 0) && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground py-8"
                >
                  No accounts found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-4">
        {accounts?.data.map((account) => (
          <Card key={account.id} className="p-4">
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {account.wa_id || "No phone number"}
                  </div>
                </div>
                <Badge
                  variant={
                    account.status === "connected"
                      ? "success"
                      : account.status === "disconnected"
                      ? "destructive"
                      : "secondary"
                  }
                  className="capitalize"
                >
                  {account.status}
                </Badge>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="font-medium text-muted-foreground">
                    Privacy
                  </div>
                  <div className="capitalize">
                    {account.settings?.privacy_mode || "Custom"}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">
                    External ID
                  </div>
                  <div className="flex items-center">
                    <CopyIdButton id={account.external_id} className="h-6 text-xs" />
                  </div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">
                    Account ID
                  </div>
                  <div className="flex items-center">
                    <CopyIdButton id={account.id} className="h-6 text-xs" />
                  </div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">
                    Total Contacts
                  </div>
                  <div>{getContactsCount(account.id)}</div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center text-sm text-muted-foreground pt-2 border-t">
                <div>
                  Last updated:{" "}
                  {account.deleted_at ? (
                    <span className="text-destructive">Deleted</span>
                  ) : (
                    format(new Date(), "PPP")
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => downloadContactsAsCsv(account.id, account.name)}
                  disabled={account.status !== "connected" || loadingCsv === account.id}
                >
                  {loadingCsv === account.id ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {(!accounts?.data || accounts.data.length === 0) && (
          <div className="text-center text-muted-foreground py-8">
            No accounts found
          </div>
        )}
      </div>
    </div>
  );
}
